import type { BacktestDetail, BacktestRequest, BacktestResult, BacktestStrategy, BacktestSummary, ExitConfigDto } from '@et/shared';
import { useEffect, useState } from 'react';
import { api, describeError } from '../api/client';
import { BacktestChart } from '../components/BacktestChart';
import { Coin } from '../components/CoinLogo';
import { CoinBars } from '../components/ReportCharts';
import { dateTime, money, signed, tone } from '../lib/format';
import { useT } from '../lib/i18n';
import { toast } from '../lib/toast';
import { useResource } from '../lib/use-resource';

/**
 * Backtests: replay history through a strategy and see — net of fees and slippage — what it
 * would have earned next to simply holding BTC, with an honest verdict on whether the result
 * means anything (sample size, baselines, cost stress; see the backtest-audit checklist).
 */

const STRATEGIES: { key: BacktestStrategy; label: string; hint: string }[] = [
  { key: 'trend_follower', label: 'Trend follower (daily)', hint: 'Buys coins breaking out to new highs and holds while the trend lasts. Research-backed; tested over years.' },
  { key: 'technical_hunter', label: 'Chart hunter (5 min)', hint: 'The live chart strategy: buys oversold dips on 5-minute bars. Up to 180 days, coins × days at most 1800.' },
];
const INTRADAY: readonly BacktestStrategy[] = ['technical_hunter'];

const VERDICT_LABEL: Record<string, string> = { trusted: 'Result looks reliable', suspect: 'Treat with caution', invalid: 'Not enough evidence' };
const REASON_LABEL: Record<string, string> = {
  too_few_trades: 'Only {value} trades — fewer than 30 prove nothing.',
  few_trades: '{value} trades — at least 100 are needed to rely on it.',
  sharpe_too_high: 'The risk-adjusted return looks too good to be true.',
  win_rate_too_high: 'Win rate above 90% — suspiciously high.',
  below_baseline: 'Did not beat: {value}.',
  not_profitable: 'It lost money after fees — beating the comparisons below is not an edge.',
  fails_cost_stress: 'The profit disappears if fees and slippage are 1.5× higher.',
};
const BASELINE_LABEL: Record<string, string> = { btc_hold: 'Holding BTC', equal_weight_hold: 'Holding every coin equally', random_entries: 'Buying at random times' };
const WARNING_LABEL: Record<string, string> = {
  survivorship: "Coins were picked from today's Binance list, so coins that were delisted since are missing. Real results would likely be somewhat worse.",
  gates_not_simulated: 'Live gates need order books and fresh tickers: the spread is charged as a fixed cost instead, and an enabled BTC check is approximated on the test’s own candles.',
  btc_regime_approx_5m: 'The Bitcoin check uses 5-minute candles here; live it uses 1-minute ones.',
  btc_regime_approx_1m: 'The Bitcoin check uses 1-minute candles, as live.',
};

const iso = (d: Date): string => d.toISOString().slice(0, 10);
const daysAgo = (n: number): string => iso(new Date(Date.now() - n * 86_400_000));

export function Backtest(): JSX.Element {
  const tr = useT();
  const runs = useResource<BacktestSummary[]>('/backtests');
  const exits = useResource<ExitConfigDto[]>('/exits');
  const [strategy, setStrategy] = useState<BacktestStrategy>('trend_follower');
  const [from, setFrom] = useState('2022-01-01');
  const [to, setTo] = useState(daysAgo(1));
  const [topN, setTopN] = useState('20');
  const [capital, setCapital] = useState('10000');
  const [feePct, setFeePct] = useState('0.1');
  const [exitId, setExitId] = useState('');
  const [spreadBps, setSpreadBps] = useState('10');
  const [trend, setTrend] = useState({ maxCoinPct: '20', targetVolPct: '60', btcFilter: true, minMedianVolumeUsdt: '10000000' });
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<BacktestDetail | null>(null);
  const [busy, setBusy] = useState(false);

  const pick = (s: BacktestStrategy): void => {
    setStrategy(s);
    if (INTRADAY.includes(s)) {
      setFrom(daysAgo(60));
      setTopN('20');
    } else setFrom('2022-01-01');
  };

  // Poll the selected run until it finishes.
  useEffect(() => {
    if (!selected) return;
    let alive = true;
    let timer: number | undefined;
    const load = async (): Promise<void> => {
      try {
        const d = await api.get<BacktestDetail>(`/backtests/${selected}`);
        if (!alive) return;
        setDetail(d);
        if (d.status === 'queued' || d.status === 'running') timer = window.setTimeout(() => void load(), 2000);
        else void runs.reload();
      } catch (err) {
        if (alive) toast.error(describeError(err));
      }
    };
    void load();
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [selected]);

  const run = async (): Promise<void> => {
    const body: Partial<BacktestRequest> & Record<string, unknown> = { strategy, from, to, topN: Number(topN), initialCapital: capital, feePct };
    if (strategy === 'trend_follower') body.params = { ...trend };
    const exit = exits.data?.find((e) => e.id === exitId);
    if (INTRADAY.includes(strategy)) {
      body.spreadBps = Number(spreadBps);
      if (exit) body.exit = { exitKey: exit.exitKey, params: exit.params };
    }
    setBusy(true);
    try {
      const s = await api.post<BacktestSummary>('/backtests', body);
      setSelected(s.id);
      void runs.reload();
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <h1>{tr('Backtest')}</h1>
      <div className="muted">{tr('A backtest replays the past through a strategy: what would it have earned, after Binance fees, compared with simply holding BTC? It never places orders.')}</div>

      <div className="card stack">
        <div className="row" role="group" aria-label={tr('Strategy')}>
          {STRATEGIES.map((s) => <button key={s.key} className={`small ${s.key === strategy ? 'primary' : ''}`} onClick={() => { pick(s.key); }}>{tr(s.label)}</button>)}
        </div>
        <div className="muted">{tr(STRATEGIES.find((s) => s.key === strategy)?.hint ?? '')}</div>
        <div className="form-grid">
          <label className="field">{tr('From')}<input type="date" value={from} onChange={(e) => { setFrom(e.target.value); }} /></label>
          <label className="field">{tr('To')}<input type="date" value={to} onChange={(e) => { setTo(e.target.value); }} /></label>
          <label className="field">{tr('Coins')}<input className="mono" inputMode="numeric" value={topN} onChange={(e) => { setTopN(e.target.value); }} /><span className="hint">{tr('The most traded USDT pairs.')}</span></label>
          <label className="field">{tr('Starting capital (USDT)')}<input className="mono" inputMode="decimal" value={capital} onChange={(e) => { setCapital(e.target.value); }} /></label>
          <label className="field">{tr('Binance fee per trade (%)')}<input className="mono" inputMode="decimal" value={feePct} onChange={(e) => { setFeePct(e.target.value); }} /><span className="hint">{tr('0.1 normally, 0.075 when paying fees in BNB.')}</span></label>
          {INTRADAY.includes(strategy) && (
            <>
              <label className="field">
                {tr('Exit strategy')}
                <select value={exitId} onChange={(e) => { setExitId(e.target.value); }}>
                  <option value="">{tr('The one in use')}</option>
                  {exits.data?.map((e) => <option key={e.id} value={e.id}>{tr(e.exitKey)}</option>)}
                </select>
              </label>
              <label className="field">{tr('Spread (bps)')}<input className="mono" inputMode="numeric" value={spreadBps} onChange={(e) => { setSpreadBps(e.target.value); }} /><span className="hint">{tr('Gap between buy and sell price, paid half on each side. 10 bps = 0.1%.')}</span></label>
            </>
          )}
          {strategy === 'trend_follower' && (
            <>
              <label className="field">{tr('Max per coin (%)')}<input className="mono" inputMode="decimal" value={trend.maxCoinPct} onChange={(e) => { setTrend({ ...trend, maxCoinPct: e.target.value }); }} /></label>
              <label className="field">{tr('Target volatility (%)')}<input className="mono" inputMode="decimal" value={trend.targetVolPct} onChange={(e) => { setTrend({ ...trend, targetVolPct: e.target.value }); }} /><span className="hint">{tr('More volatile coins get a smaller position.')}</span></label>
              <label className="field">{tr('Minimum daily volume (USDT)')}<input className="mono" inputMode="decimal" value={trend.minMedianVolumeUsdt} onChange={(e) => { setTrend({ ...trend, minMedianVolumeUsdt: e.target.value }); }} /></label>
              <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={trend.btcFilter} onChange={(e) => { setTrend({ ...trend, btcFilter: e.target.checked }); }} />
                {tr('No new buys while BTC is below its 200-day average')}
              </label>
            </>
          )}
        </div>
        <div><button className="primary" disabled={busy} onClick={() => void run()}>{busy ? tr('Working…') : tr('Run backtest')}</button></div>
      </div>

      {detail && <RunView d={detail} />}

      <div className="card">
        <h2>{tr('Previous backtests')}</h2>
        {runs.data && runs.data.length === 0 && <div className="empty">{tr('No backtests yet.')}</div>}
        <div className="stack run-list" style={{ gap: 6 }}>
          {runs.data?.map((r) => (
            <button key={r.id} className={r.id === selected ? 'selected' : ''} onClick={() => { setSelected(r.id); }}>
              <b>{tr(STRATEGIES.find((s) => s.key === r.strategy)?.label ?? r.strategy)}</b>{' '}
              <span className="muted">{r.request.from} → {r.request.to} · {dateTime(r.createdAt)}</span>{' '}
              {r.headline ? (
                <span className={tone(r.headline.totalReturnPct)}>{signed(r.headline.totalReturnPct)}% <span className="muted">({tr('BTC')} {signed(r.headline.btcHoldReturnPct)}%)</span></span>
              ) : (
                <span className="muted">{tr(r.status)}</span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function RunView({ d }: { d: BacktestDetail }): JSX.Element {
  const tr = useT();
  if (d.status === 'failed') return <div className="card neg">{tr('Backtest failed')}: {d.error}</div>;
  if (d.status !== 'done' || !d.result) {
    return (
      <div className="card stack">
        <div>{tr('Running… downloading history and replaying the strategy. This can take a few minutes.')}</div>
        <div className="progress"><div style={{ width: `${String(d.progress)}%` }} /></div>
      </div>
    );
  }
  return <ResultView r={d.result} />;
}

function ResultView({ r }: { r: BacktestResult }): JSX.Element {
  const tr = useT();
  const m = r.metrics;
  const [allTrades, setAllTrades] = useState(false);
  return (
    <div className="stack">
      <div className={`card stack verdict ${r.verdict}`}>
        <b>{tr(VERDICT_LABEL[r.verdict] ?? r.verdict)}</b>
        {r.verdictReasons.length > 0 && (
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {r.verdictReasons.map((x) => <li key={x.code + (x.value ?? '')}>{tr(REASON_LABEL[x.code] ?? x.code, { value: x.code === 'below_baseline' ? tr(BASELINE_LABEL[x.value ?? ''] ?? x.value ?? '') : (x.value ?? '') })}</li>)}
          </ul>
        )}
        {r.warnings.map((w) => <div key={w} className="muted">⚠︎ {w.startsWith('no_data:') ? tr('No history for {symbol}; left out.', { symbol: w.slice(8) }) : tr(WARNING_LABEL[w] ?? w)}</div>)}
        <div className="muted" style={{ fontSize: 12 }}>{tr('Backtests run for this strategy so far: {n}. The more variations you try, the more likely the best one is luck.', { n: r.trials })}</div>
      </div>

      <div className="grid tiles">
        <div className="card tile"><div className="label">{tr('Result')}</div><div className={`value ${tone(m.totalReturnPct)}`}>{signed(m.totalReturnPct)}%</div><div className="muted">{money(m.startEquity, 0)} → {money(m.endEquity, 0)} USDT</div></div>
        <div className="card tile"><div className="label">{tr('Holding BTC instead')}</div><div className={`value ${tone(r.benchmark.btcHoldReturnPct)}`}>{signed(r.benchmark.btcHoldReturnPct)}%</div><div className="muted">{tr('worst fall {p}%', { p: money(r.benchmark.btcHoldMaxDrawdownPct) })}</div></div>
        <div className="card tile"><div className="label">{tr('Per year')}</div><div className={`value ${tone(m.cagrPct)}`}>{m.cagrPct ? `${signed(m.cagrPct)}%` : '—'}</div><div className="muted">{tr('annualised')}</div></div>
        <div className="card tile"><div className="label">{tr('Worst fall from a peak')}</div><div className="value neg">{money(m.maxDrawdownPct)}%</div><div className="muted">{tr('longest losing streak: {n}', { n: m.longestLosingStreak })}</div></div>
        <div className="card tile"><div className="label">{tr('Trades')}</div><div className="value">{m.trades}</div><div className="muted">{m.winRatePct ? tr('win rate {p}%', { p: money(m.winRatePct, 1) }) : '—'}</div></div>
        <div className="card tile"><div className="label">{tr('Fees paid')}</div><div className="value">{money(m.feesPaid, 0)}</div><div className="muted">USDT</div></div>
      </div>

      <div className="card">
        <h2>{tr('Account value: strategy vs. holding BTC')}</h2>
        <BacktestChart points={r.equity} />
      </div>

      <div className="grid cols-2">
        <div className="card">
          <h2>{tr('Compared with doing nothing clever')}</h2>
          <table>
            <tbody>
              <tr><td><b>{tr('This strategy')}</b></td><td className={`num ${tone(m.totalReturnPct)}`}><b>{signed(m.totalReturnPct)}%</b></td></tr>
              {r.baselines.map((b) => <tr key={b.key}><td>{tr(BASELINE_LABEL[b.key] ?? b.key)}</td><td className={`num ${tone(b.returnPct)}`}>{signed(b.returnPct)}%</td></tr>)}
              <tr><td>{tr('This strategy with 1.5× fees')}</td><td className={`num ${tone(r.stressedReturnPct)}`}>{signed(r.stressedReturnPct)}%</td></tr>
            </tbody>
          </table>
          <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
            {tr('Avg win {w} · avg loss {l} · profit factor {pf} · Sharpe {s} · invested on average {x}% of the time', { w: money(m.avgWin), l: money(m.avgLoss), pf: m.profitFactor ?? '—', s: m.sharpe ?? '—', x: money(m.avgExposurePct, 0) })}
          </div>
        </div>
        <div className="card">
          <h2>{tr('Year by year')}</h2>
          <table>
            <thead><tr><th>{tr('Period')}</th><th className="num">{tr('Strategy')}</th><th className="num">{tr('Worst fall')}</th><th className="num">BTC</th></tr></thead>
            <tbody>
              {r.periods.map((p) => (
                <tr key={p.label}><td>{tr(p.label)}</td><td className={`num ${tone(p.returnPct)}`}>{signed(p.returnPct)}%</td><td className="num muted">{money(p.maxDrawdownPct)}%</td><td className={`num ${tone(p.btcHoldReturnPct)}`}>{signed(p.btcHoldReturnPct)}%</td></tr>
              ))}
            </tbody>
          </table>
          <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>{tr('A real edge should hold up in more than one kind of market.')}</div>
        </div>
      </div>

      <div className="card">
        <h2>{tr('Which coin earned or lost how much')}</h2>
        <CoinBars rows={r.bySymbol} />
      </div>

      <div className="card">
        <div className="row" style={{ marginBottom: 12 }}>
          <h2 style={{ margin: 0 }}>{tr('Trades')}</h2>
          <span className="muted">{tr('{n} trades', { n: m.trades })}</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>{tr('Coin')}</th><th>{tr('Bought')}</th><th>{tr('Sold')}</th><th className="num">{tr('Buy price')}</th><th className="num">{tr('Sell price')}</th><th className="num">{tr('Invested')}</th><th className="num">{tr('Profit / loss')}</th><th className="num">%</th><th className="num">{tr('Best')}</th><th className="num">{tr('Worst')}</th><th>{tr('Reason')}</th></tr></thead>
            <tbody>
              {(allTrades ? r.trades : r.trades.slice(0, 50)).map((x) => (
                <tr key={`${x.symbol}-${x.entryAt}-${x.exitAt}`}>
                  <td><Coin symbol={x.symbol} /></td>
                  <td className="muted">{dateTime(x.entryAt)}</td>
                  <td className="muted">{dateTime(x.exitAt)}</td>
                  <td className="num">{x.entryPrice}</td>
                  <td className="num">{x.exitPrice}</td>
                  <td className="num">{money(x.invested)}</td>
                  <td className={`num ${tone(x.pnl)}`}><b>{signed(x.pnl)}</b></td>
                  <td className={`num ${tone(x.pnlPct)}`}>{signed(x.pnlPct)}</td>
                  <td className="num pos">{x.mfePct == null ? '—' : `${signed(x.mfePct)}%`}</td>
                  <td className="num neg">{x.maePct == null ? '—' : `${signed(x.maePct)}%`}</td>
                  <td className="muted">{tr(x.reason)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!allTrades && r.trades.length > 50 && <button className="small" style={{ marginTop: 8 }} onClick={() => { setAllTrades(true); }}>{tr('Show all {n}', { n: r.trades.length })}</button>}
      </div>

      <details className="card">
        <summary>{tr('Test details')}</summary>
        <pre className="mono" style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{JSON.stringify({ params: r.params, revision: r.revision, coins: r.symbols }, null, 2)}</pre>
      </details>
    </div>
  );
}

// Labels built from lookup tables — listed so the i18n coverage test sees them.
export const BACKTEST_LABELS = [
  ...STRATEGIES.flatMap((s) => [s.label, s.hint]),
  ...Object.values(VERDICT_LABEL),
  ...Object.values(REASON_LABEL),
  ...Object.values(BASELINE_LABEL),
  ...Object.values(WARNING_LABEL),
  'last 90 days', 'trend exit', 'end of test', 'queued', 'running', 'done', 'failed',
];
